/* global GM_STATE */
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { fetchData, postData } from 'helpers/fetch'
import { addClass, select, selectAll, removeClass } from 'helpers/dom'
import { hasOwnProperties } from 'helpers/utils'
import { getCustomBadgeClass } from 'libs/shopify'
import { updateDataReward } from 'libs/loyalty/loyalty-service'

const selectors = {
  CART_COUNT_SELECTOR: '#cart-count-span',
  MINI_CART_CONTENT_CLASS: '.js-mini-cart-content'
}

const customBadgeTags = GM_STATE?.customs?.badge.tags || []
const customBadgeItems = GM_STATE?.customs?.badge.items || []

const getActiveShopliftVariantId = async () => {
  if (!window.shoplift) return null

  const variantId = GM_STATE?.shopify?.shopliftSplitTestVariantId

  if (!variantId) return null

  try {
    const onVariant = await window.shoplift.isHypothesisActive(variantId)

    return onVariant ? variantId : null
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Shoplift test check failed:', error)

    return null
  }
}

const syncShopliftCartAttribute = async (cart) => {
  if (cart.attributes?.shoplift_split_test != null) {
    return cart
  }

  const variantId = await getActiveShopliftVariantId()

  if (!variantId) {
    return cart
  }

  const updatedCart = await postData(
    `${window.Shopify.routes.root}cart/update.js`,
    { attributes: { shoplift_split_test: variantId } },
    'json',
    cartError
  )

  return updatedCart || cart
}

const cartError = async (response) => {
  const error = await response.json()

  globalEmitter.emit(emitterProps.notice.global, {
    type: 'error',
    content: error.description
  })
  throw new Error(error.description)
}

const getNativeCart = async () => {
  return fetchData('/cart.js', cartError).then((cart) => cart)
}

const getCart = async () => {
  return fetchData(`${window.Shopify.routes.root}cart?view=ajax`, cartError).then((cart) => {
    if (cart?.count) {
      globalEmitter.emit(emitterProps.cart.count, cart.count)
    }

    return cart
  })
}

const getLineItem = (id, properties, quantity, sellingPlanId = null) => {
  const data = {
    items: [
      {
        id,
        properties,
        quantity
      }
    ]
  }

  if (sellingPlanId) {
    data.items[0].selling_plan = sellingPlanId
  }

  return data
}

const addCartItem = async (id, properties = {}, quantity = 1, sellingPlanId = null) => {
  const lineItem = getLineItem(id, properties, quantity, sellingPlanId)

  return postData(`${window.Shopify.routes.root}cart/add.js`, lineItem, 'json', cartError).then((item) => {
    return item
  })
}

const addMultipleCartItems = async (items) => {
  return postData(`${window.Shopify.routes.root}cart/add.js`, items, 'json', async (response) => {
    const { description } = await response.json()

    globalEmitter.emit(emitterProps.notice.global, {
      type: 'error',
      content: description
    })
  }).then((items) => {
    return items
  })
}

const updateMultipleCartItems = async (items) => {
  return postData(`${window.Shopify.routes.root}cart/update.js`, items, 'json', async (response) => {
    const { description } = await response.json()

    globalEmitter.emit(emitterProps.notice.global, {
      type: 'error',
      content: description
    })
  }).then((items) => {
    return items
  }).catch((error) => cartError(error))
}

const updateCartItem = async (id, quantity) => {
  return postData(`${window.Shopify.routes.root}cart/change.js`, {
    id,
    quantity
  }, 'json', cartError).then((cart) => {
    globalEmitter.emit(emitterProps.cart.change, cart.item_count)

    return cart
  })
}

const changeQuantity = async (id, quantity) => {
  return postData(`${window.Shopify.routes.root}cart/change.js`, {
    id,
    quantity
  }, 'json', cartError).then((cart) => {
    return cart
  })
}

const removeCartItem = async (id) => {
  return postData(`${window.Shopify.routes.root}cart/change.js`, {
    id,
    quantity: 0
  }, 'json', cartError).then((cart) => {
    if (cart?.count) {
      globalEmitter.emit(emitterProps.cart.change, cart.item_count)
    }

    return cart
  })
}

const clearCart = async () => {
  return postData(`${window.Shopify.routes.root}cart/clear.js`, {}, 'json', cartError).then((cart) => {
    globalEmitter.emit(emitterProps.cart.clear, cart)

    return cart
  })
}

const changeCartItemSellingPlan = (payload) => {
  return postData(`${window.Shopify.routes.root}cart/change.js`, payload, 'json', cartError).then((cart) => {
    globalEmitter.emit(emitterProps.cart.change, cart.item_count)

    return cart
  })
}

const reRenderCartCount = (count) => {
  const els = selectAll(selectors.CART_COUNT_SELECTOR, document.body)

  if (els) {
    if (count === 0) {
      els.forEach((el) => addClass('hidden', el))
    } else {
      els.forEach((el) => {
        el.innerText = count
        removeClass('hidden', el)
        addClass('flex', el)
      })
    }
  }
}

const updateCart = async () => {
  let cart = await getCart()

  if (!cart) return cart

  if (GM_STATE?.shopify?.isShopliftSplitTestEnabled) {
    try {
      cart = (await syncShopliftCartAttribute(cart)) || cart
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Shoplift cart attribute sync failed', error)
    }
  }

  reRenderCartCount(cart.item_count)
  updateDataReward(cart)

  return cart
}

const forceReloadItem = async (item) => {
  const currentQty = item.quantity
  const nextQty = item.quantity + 1

  await changeQuantity(item.key, nextQty)

  const cart = await changeQuantity(item.key, currentQty)

  return cart
}

const openMiniCart = () => {
  const el = select('cart-slideout modal-dialog', document.body)

  if (el) {
    el.show()

    const contentEl = select(selectors.MINI_CART_CONTENT_CLASS, el)

    if (contentEl) {
      contentEl.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }
  }
}

const getCartBadges = (tags, properties) => {
  const items = []

  if (!tags.length && !properties) {
    return items
  }

  if (tags.length) {
    for (const tag of tags) {
      const index = customBadgeTags.findIndex((i) => tag === i)

      if (index !== -1 && !customBadgeItems[index].hideOnCart) {
        items.push(customBadgeItems[index])
      } else {
        if (tag.includes('label-')) {
          const tagName = tag.replace('label-', '')

          items.push({
            text: tagName,
            tag,
            customClass: getCustomBadgeClass(tagName),
            backgroundColor: '',
            textColor: ''
          })
        }
      }
    }
  }

  return items.filter((item) => item)
}

const getCartProperties = (items) => {
  const arrs = []

  // Properties to hide from cart display (internal use only)
  const hiddenProperties = [
    '_first_subscription_gift_variant_id',
    '_first_subscription_gift_product_id'
  ]

  if (items && Object.keys(items).length > 0) {
    for (const [key, value] of Object.entries(items)) {
      if (!hiddenProperties.includes(key)) {
        arrs.push({
          name: key,
          key: value
        })
      }
    }
  }

  return arrs
}

const getDisableChangeQuantity = (properties) => {
  return properties && properties?._disable_quantity === 'true'
}

const getItemsByKey = (items, item, key) => items.filter((cartItem) => cartItem.properties && hasOwnProperties(key, cartItem.properties) && cartItem.properties.timestamp === item.properties.timestamp)

const getMaxQuantity = (cartItem) => {
  return cartItem.inventory_management === 'shopify' && cartItem.inventory_policy === 'deny' && cartItem.inventory_quantity > 0
    ? cartItem.inventory_quantity
    : 1000
}

const removeBundlesItem = async (item) => {
  const newCart = await updateCart()
  const bundleItems = getItemsByKey(newCart.items, item, 'timestamp')

  if (bundleItems.length === 0) {
    return
  }

  const newUpdates = {}

  for (const itemCart of bundleItems) {
    newUpdates[itemCart.id] = 0
  }

  const body = {
    updates: newUpdates
  }

  await updateMultipleCartItems(body)
}

const getMaxQuantityByVariant = (cartItems, variant) => {
  if (!cartItems.length) return variant.inventory_quantity

  const currentItem = cartItems.find((item) => item.id === variant.id)

  if (variant.inventory_policy === 'continue') return 9999

  if (!currentItem) return variant.inventory_quantity

  const gap = variant.inventory_quantity - currentItem.quantity

  return gap
}

export {
  getNativeCart,
  getCart,
  addCartItem,
  updateCartItem,
  removeCartItem,
  clearCart,
  updateCart,
  openMiniCart,
  getCartBadges,
  getCartProperties,
  addMultipleCartItems,
  getDisableChangeQuantity,
  forceReloadItem,
  updateMultipleCartItems,
  getItemsByKey,
  getMaxQuantity,
  removeBundlesItem,
  reRenderCartCount,
  getMaxQuantityByVariant,
  changeCartItemSellingPlan
}
